import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { CustomerCatalogueProvider } from '@/services/catalogue/CustomerCatalogueService';
import { FC } from 'react';
import { Outlet, useParams } from 'react-router';
import { CatalogueProvider } from '../../services/catalogue/CatalogueService';
import {
  AdminCatalogueConfiguration,
  CompanyCatalogueConfiguration,
  PartnerCatalogueConfiguration
} from '../../services/catalogue/catalogue';

interface CatalogueProps {
  configuration: CompanyCatalogueConfiguration | PartnerCatalogueConfiguration | AdminCatalogueConfiguration;
  purchaseOrderMode?: boolean;
}

export const Catalogue: FC<CatalogueProps> = ({ configuration, purchaseOrderMode }) => {
  return (
    <CatalogueProvider configuration={configuration} purchaseOrder={purchaseOrderMode}>
      <div className="flex h-full flex-1 flex-col bg-gradient-to-br from-slate-50 to-gray-100">
        <Outlet />
      </div>
    </CatalogueProvider>
  );
};

interface SmartCatalogueProps {
  purchaseOrderMode?: boolean;
}

export const SmartCatalogue: FC<SmartCatalogueProps> = ({ purchaseOrderMode }) => {
  const { role } = useUser();
  const { companyId, partnerId } = useParams<{ companyId?: string; partnerId?: string }>();

  // There is no customerId or partnerId, so we are in the admin catalogue
  if (!companyId && !partnerId) {
    return <Catalogue configuration={{ for: RoleType.Admin, as: role.type }} />;
  }

  // There is a partnerId but no customerId, so we are in the partner catalogue
  if (partnerId && !companyId) {
    return <Catalogue configuration={{ for: RoleType.Partner, as: role.type, partnerId }} />;
  }

  // There is a company and a partnerId, so we are in the company catalogue
  if (companyId && partnerId) {
    return (
      <CustomerCatalogueProvider customerId={companyId} customPartnerId={partnerId}>
        <Catalogue
          configuration={{ for: RoleType.Company, as: role.type, companyId, partnerId }}
          purchaseOrderMode={purchaseOrderMode}
        />
      </CustomerCatalogueProvider>
    );
  }

  throw new Error('Invalid catalogue configuration');
};

export const MyCompanyCatalogue: FC = () => {
  const { role } = useUser();
  if (!role.customerId || !role.partnerId) throw new Error('Missing customerId or partnerId');
  return (
    <Catalogue
      configuration={{ for: RoleType.Company, as: role.type, companyId: role.customerId, partnerId: role.partnerId }}
    />
  );
};
